import React from 'react';
import { createDateTimeFormatters, createFormatCurrency } from './i18n.helpers';
import { FormatCurrencyOptions } from './i18n.types';

export const useI18n = () => {
  const languageCode = 'it-IT';
  const currencyCode = 'EUR';
  return {
    formatters: {
      formatCurrency: createFormatCurrency(languageCode),
      ...createDateTimeFormatters(languageCode),
    },
    info: {
      currencyCode,
      locale: languageCode,
      decimalSeparator: ',',
      thousandSeparator: '.',
    },
  };
};

/**
 * Formats a number to 'EUR' according to the current locale.
 * @param money number to format.
 * @param options optional additional configuration.
 * @param options.hideCents If 'true' does not display cents. Default 'false'.
 * @param options.isNumberExpressedInCents If 'true' input number gets divided
 * by '100' before being formatted. Default 'true'.
 *  * @example
 * ```typescript
 * const MyComp: React.FC<{ money: string }> = ({ money }) => {
 *   const formattedCurrency = useFormatCurrency(money);
 *
 *   return <span>{formattedCurrency}</span>;
 * }
 * ```
 * @see CurrencyFormatter in './i18n.types.d.ts'
 */
export const useFormatCurrency = (
  money: number,
  options?: Partial<FormatCurrencyOptions>,
) => {
  const {
    formatters: { formatCurrency },
  } = useI18n();

  const formattedCurrency = React.useMemo(
    () => formatCurrency(money, options),
    [formatCurrency, money, options],
  );

  return formattedCurrency;
};
